import QRCode from 'qrcode';
// escposLogo.js — Render the company logo as an ESC/POS raster bit-image
// (GS v 0) string that can be prepended to a thermal bill's `content`.
//
// Returns '' (so the caller just prints text-only) when there is no logo, the
// image can't be loaded, or the canvas is tainted by CORS. The logo image
// should ideally be a clean, high-contrast (near B&W) PNG; photos print poorly
// on a 1-bit thermal head.
//
// NOTE: the print agent must write `content` to the printer as raw bytes
// (latin1 / 8-bit), NOT re-encode it as UTF-8 — otherwise raster bytes > 127
// get corrupted. Plain text bills are unaffected.

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // needed so getImageData isn't blocked
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

export async function buildLogoEscPos(logoUrl, maxWidthDots = 384) {
  if (!logoUrl) return '';
  try {
    const img = await loadImage(logoUrl);
    const srcW = img.width || maxWidthDots;
    const srcH = img.height || 0;
    if (!srcH) return '';

    // Fit to the printer width; raster width must be a multiple of 8 dots.
    let w = Math.min(maxWidthDots, srcW);
    w -= w % 8;
    if (w <= 0) return '';
    const h = Math.round((srcH / srcW) * w);
    if (h <= 0) return '';

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const { data } = ctx.getImageData(0, 0, w, h);
    const bytesPerRow = w / 8;
    let raster = '';

    for (let y = 0; y < h; y++) {
      for (let xb = 0; xb < bytesPerRow; xb++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = xb * 8 + bit;
          const idx = (y * w + x) * 4;
          const r = data[idx];
          const g = data[idx + 1];
          const b = data[idx + 2];
          const a = data[idx + 3];
          // Transparent → white. Dark pixels → black dot (bit = 1).
          const lum = a < 128 ? 255 : 0.299 * r + 0.587 * g + 0.114 * b;
          if (lum < 128) byte |= 0x80 >> bit;
        }
        raster += String.fromCharCode(byte);
      }
    }

    const xL = bytesPerRow & 0xff;
    const xH = (bytesPerRow >> 8) & 0xff;
    const yL = h & 0xff;
    const yH = (h >> 8) & 0xff;

    // Center, print raster, then reset alignment to left.
    return (
      '\x1B\x61\x01' +
      '\x1D\x76\x30\x00' +
      String.fromCharCode(xL) +
      String.fromCharCode(xH) +
      String.fromCharCode(yL) +
      String.fromCharCode(yH) +
      raster +
      '\n' +
      '\x1B\x61\x00'
    );
  } catch {
    // CORS taint, load error, etc. — fall back to a text-only bill.
    return '';
  }
}

// Build a SCANNABLE UPI QR for thermal bills: generate the QR matrix locally
// (node-qrcode) and render each module as an exact N-dot block, so modules stay
// crisp & aligned. A scaled / anti-aliased *image* QR usually won't scan on a
// 1-bit thermal head; this does. Requires:  npm install qrcode
export function buildUpiQrEscPos(text, targetWidthDots = 360) {
  if (!text) return '';
  let qr;
  try {
    qr = QRCode.create(text, { errorCorrectionLevel: 'M' });
  } catch {
    return '';
  }
  const m = qr.modules;
  const size = m.size;
  const get =
    typeof m.get === 'function'
      ? (r, c) => (m.get(r, c) ? 1 : 0)
      : (r, c) => m.data[r * size + c] & 1;
  const quiet = 4; // quiet zone (modules)
  const totalModules = size + quiet * 2;
  const dotsPerModule = Math.max(3, Math.floor(targetWidthDots / totalModules));
  const sidePx = totalModules * dotsPerModule;
  let w = sidePx;
  w += (8 - (w % 8)) % 8; // raster width must be a multiple of 8 dots
  const h = sidePx;
  const bytesPerRow = w / 8;
  let raster = '';
  for (let y = 0; y < h; y++) {
    const modRow = Math.floor(y / dotsPerModule) - quiet;
    for (let xb = 0; xb < bytesPerRow; xb++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const x = xb * 8 + bit;
        const modCol = Math.floor(x / dotsPerModule) - quiet;
        let dark = 0;
        if (modRow >= 0 && modRow < size && modCol >= 0 && modCol < size) {
          dark = get(modRow, modCol);
        }
        if (dark) byte |= 0x80 >> bit;
      }
      raster += String.fromCharCode(byte);
    }
  }
  const xL = bytesPerRow & 0xff;
  const xH = (bytesPerRow >> 8) & 0xff;
  const yL = h & 0xff;
  const yH = (h >> 8) & 0xff;
  return (
    '\x1B\x61\x01' +
    '\x1D\x76\x30\x00' +
    String.fromCharCode(xL) +
    String.fromCharCode(xH) +
    String.fromCharCode(yL) +
    String.fromCharCode(yH) +
    raster +
    '\n' +
    '\x1B\x61\x00'
  );
}